import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import "./studio.css";
import { STEP_DEFS, REVIEW_STEP_NUMBER, ROUNDS_STEP_NUMBER, defaultRound, defaultRoundContent, buildTeams, stepNumberFor, buildJoinLink, isContentValidForType, migrateLegacyInfoRounds } from "./constants";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { saveHuntQuestions, fetchHuntQuestions } from "../../functions/huntQuestions";
import { subscribeHuntStart, subscribeHuntPlayers } from "../../../utils/huntFirebase";
import { TourStartButton } from "../../tour/AdminTour";

import GameBasics from "./steps/GameBasics";
import SettingLocations from "./steps/SettingLocations";
import RoundsContent from "./steps/RoundsContent";
import ReviewContent from "./steps/ReviewContent";
import Teams from "./steps/Teams";
import RouteAssignment from "./steps/RouteAssignment";
import Publish from "./steps/Publish";

const DEFAULT_HUNT = {
  huntName: "",
  client: "",
  title: "",
  subHeading: "",
  textColor: "#1A1A1A",
  headingColor: "#1A1A1A",
  buttonBgColor: "#D83307",
  buttonTextColor: "#FFFFFF",
  setting: "office",
  checkpoints: [],
  numRounds: 6,
  rounds: [1, 2, 3, 4, 5, 6].map((n) => defaultRound(`r${n}`)),
  scoringModel: "points_time",
  contentBrief: "",
  difficulty: "balanced",
  tone: "playful",
  questionSource: "ai",
  roundContent: [],
  numTeams: 8,
  joinMethod: "shared_link",
  teams: [],
  lobbyCode: "",
  manualRoutes: false,
  routes: [],
  published: false,
  publishedAt: null,
  wizardStep: 1,
  instructions: ["", "", "", "", "", ""],
  liveLeaderboardEnabled: true,
  liveTimeVisible: true,
  approveContentEnabled: false,
};

const HEX_COLOR_KEYS = ["textColor", "headingColor", "buttonBgColor", "buttonTextColor"];

function sanitizeHex(value, fallback) {
  return typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value) ? value : fallback;
}

const HUNT_FIELD_KEYS = Object.keys(DEFAULT_HUNT);

// Every flat hunt field is stored as a raw themesdata row and read back through a generic
// `JSON.parse(value)` (with a string fallback if parsing throws). That's correct for the array/
// object fields, but a plain string field whose value happens to look like JSON — a purely
// numeric lobby code ("512336"), or literally "true"/"false"/"null" — silently comes back as a
// number/boolean/null instead of a string. Force every field back to its intended type here so
// no downstream code (e.g. `.split()` on lobbyCode) can be handed the wrong one.
const STRING_KEYS = [
  "huntName",
  "client",
  "title",
  "subHeading",
  "setting",
  "scoringModel",
  "contentBrief",
  "difficulty",
  "tone",
  "questionSource",
  "joinMethod",
  "lobbyCode",
  ...HEX_COLOR_KEYS,
];
const BOOLEAN_KEYS = [
  "manualRoutes",
  "published",
  "liveLeaderboardEnabled",
  "liveTimeVisible",
  "approveContentEnabled",
];

function coerceString(value, fallback) {
  if (value == null) return fallback;
  return typeof value === "string" ? value : String(value);
}
function coerceBoolean(value, fallback) {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return fallback;
}

/** The theme data object is flat (huntName, checkpoints, ... sit alongside themeName, themeImage,
 * etc.) — pick out just the hunt-wizard fields, since that's all the wizard's local state cares about. */
function normalizeHunt(themeData) {
  const merged = { ...DEFAULT_HUNT };
  if (themeData && typeof themeData === "object") {
    for (const key of HUNT_FIELD_KEYS) {
      if (themeData[key] !== undefined) merged[key] = themeData[key];
    }
  }
  for (const key of STRING_KEYS) {
    merged[key] = coerceString(merged[key], DEFAULT_HUNT[key]);
  }
  for (const key of BOOLEAN_KEYS) {
    merged[key] = coerceBoolean(merged[key], DEFAULT_HUNT[key]);
  }
  // Hunts saved before these colour fields existed (or from a skeleton mid-migration) can be
  // missing them or hold a stale/invalid value — never let a downstream contrast check see that.
  for (const key of HEX_COLOR_KEYS) {
    merged[key] = sanitizeHex(merged[key], DEFAULT_HUNT[key]);
  }
  return merged;
}

const ROUTES_STEP_NUMBER = stepNumberFor("routes");
const LOCATIONS_STEP_NUMBER = stepNumberFor("locations");

function clampStep(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 1) return 1;
  if (v > STEP_DEFS.length) return STEP_DEFS.length;
  return v;
}

/** How far into the wizard this hunt has actually been built, going purely off what's saved —
 * not `wizardStep` (see the hydration effect's comment on why that alone isn't reliable). Used
 * to unlock the sidebar so a fully (or mostly) built hunt is never locked back down to step 1
 * just because the admin last saved from an earlier step. */
function furthestStepReachedFor(h) {
  if (h.published) return STEP_DEFS.length;
  let max = 1;
  if ((h.checkpoints || []).length > 0) max = Math.max(max, stepNumberFor("locations"));
  if ((h.roundContent || []).some((rc) => rc?.question)) max = Math.max(max, stepNumberFor("rounds"));
  if ((h.roundContent || []).length > 0 && h.roundContent.every((rc) => rc?.approved)) {
    max = Math.max(max, stepNumberFor("review"));
  }
  if (h.lobbyCode) max = Math.max(max, stepNumberFor("teams"));
  if ((h.routes || []).length > 0) max = Math.max(max, stepNumberFor("routes"));
  return max;
}

function timeAgoLabel(ts) {
  if (!ts) return null;
  const seconds = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds} sec ago`;
  const mins = Math.round(seconds / 60);
  return `${mins} min ago`;
}

function Studio() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, status, data } = useSelector((state) => state.theme);

  const [hunt, setHunt] = useState(DEFAULT_HUNT);
  const [themeMeta, setThemeMeta] = useState(null);
  const [step, setStep] = useState(1);
  const [maxStepReached, setMaxStepReached] = useState(1);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [, forceTick] = useState(0);
  const huntLoadedRef = useRef(false); // true only once hydration has genuinely succeeded — gates persistHunt
  const hydratingRef = useRef(false); // true only while a hydration attempt is in flight — prevents re-entry
  const skipNextAutosaveRef = useRef(false);
  const emptyDataRetriesRef = useRef(0);

  const [liveStarted, setLiveStarted] = useState(false);
  const [liveTeamsCount, setLiveTeamsCount] = useState(null);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    document.title = hunt.huntName?.trim() ? `${hunt.huntName} — Studio` : "ExtraMile Play Studio";
  }, [hunt.huntName]);

  // Live Firebase status shown in the header — same "hunt started" flag and team presence Live
  // Host and the players' Waiting Room use (see src/utils/huntFirebase.js).
  useEffect(() => {
    if (!organizationId || !sessionId) return undefined;
    const unsubStart = subscribeHuntStart(organizationId, sessionId, (huntStart) => {
      setLiveStarted(!!huntStart?.startedAt);
    });
    const unsubPlayers = subscribeHuntPlayers(organizationId, sessionId, (players) => {
      setLiveTeamsCount(Object.keys(players || {}).length);
    });
    return () => {
      unsubStart();
      unsubPlayers();
    };
  }, [organizationId, sessionId]);

  useEffect(() => {
    if (!data) return;
    // The backend returns `data: {}` (still truthy!) when it briefly can't resolve which theme
    // is selected — e.g. right after a refresh, before `currentTheme` has settled. Latching onto
    // that as if it were the real hunt would lock in an empty skeleton (via huntLoadedRef below)
    // and permanently ignore the actual data when it arrives moments later — and worse, the
    // autosave would then persist that empty skeleton over whatever was really saved. Only treat
    // a response that actually names a theme as real enough to hydrate from.
    const looksReal = data.themename != null || data.themeName != null;
    if (!looksReal) {
      // Nothing else re-triggers this fetch (the dispatch effect only re-fires if `currentTheme`
      // itself changes) — without a retry, landing here right when the backend hiccups leaves the
      // wizard stuck showing blank/default data forever, looking exactly like the saved hunt
      // (checkpoints, rounds, teams — all live to players right now) had disappeared. Retry a
      // few times with a short delay instead of giving up after the one unlucky response.
      if (emptyDataRetriesRef.current >= 5) return undefined;
      emptyDataRetriesRef.current += 1;
      const t = setTimeout(() => {
        dispatch(fetchThemeData({ themeId: currentTheme }));
      }, 700);
      return () => clearTimeout(t);
    }
    emptyDataRetriesRef.current = 0;
    setThemeMeta(data);
    if (hydratingRef.current || huntLoadedRef.current) return;
    hydratingRef.current = true; // claim immediately so a second `data` update mid-fetch can't re-enter
    let cancelled = false;
    (async () => {
      const nh = normalizeHunt(data);
      // Round content now lives in the `questions` table, not the flat themesdata blob —
      // fetch it separately and overlay it onto the skeleton before showing the wizard. Retry a
      // few times before giving up, since a single transient failure here used to permanently
      // blank every clue/question/answer on screen (still safely saved server-side, just never
      // re-fetched) until a hard refresh.
      let qResp = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          qResp = await fetchHuntQuestions({
            payload: {
              organizationId: organizationId || "admin",
              sessionId: sessionId || "admin",
              currentTheme,
            },
            token: adminToken,
          });
          break;
        } catch (e) {
          console.error(`[Studio] fetchHuntQuestions failed (attempt ${attempt + 1}/3):`, e.message);
          if (attempt < 2) await new Promise((r) => setTimeout(r, 600));
        }
      }
      if (cancelled) return;
      if (!qResp) {
        // Every retry failed — do NOT mark the hunt as loaded (persistHunt refuses to save until
        // huntLoadedRef is true) and do NOT setHunt with blank round content, or the very next
        // save would overwrite the real saved clues/questions/answers with nothing. Release the
        // in-flight claim so the next `data` update (or a manual retry) gets a fresh attempt.
        hydratingRef.current = false;
        Swal.fire(
          "Couldn't load round content",
          "Your saved clues/questions/answers didn't load — please refresh before making changes here.",
          "warning"
        );
        return;
      }
      if (Array.isArray(qResp.roundContent) && qResp.roundContent.length > 0) {
        nh.roundContent = qResp.roundContent;
      }
      const migrated = migrateLegacyInfoRounds(nh.rounds, nh.roundContent);
      nh.rounds = migrated.rounds;
      nh.roundContent = migrated.roundContent;
      huntLoadedRef.current = true; // only now is it safe for persistHunt to save
      skipNextAutosaveRef.current = true; // this is freshly-loaded data, not an edit to save back
      setHunt(nh);
      const s = clampStep(nh.wizardStep);
      setStep(s);
      // `wizardStep` tracks whichever step the admin was LAST on, not the furthest one they've
      // ever reached — every visit to an earlier step (e.g. tweaking Game Basics after the hunt
      // is already built and live) overwrites it back down. Using it alone as the sidebar's
      // unlock threshold meant a fully built hunt could come back from Live Host looking locked
      // down to step 1, with every later step's saved content unreachable even though it's all
      // still there. Derive the real furthest-reached step from what's actually been saved.
      setMaxStepReached(Math.max(s, furthestStepReachedFor(nh)));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Re-render every 20s so "Draft saved Xs ago" stays fresh.
  useEffect(() => {
    const id = setInterval(() => forceTick((t) => t + 1), 20000);
    return () => clearInterval(id);
  }, []);

  const updateHunt = useCallback((patch) => {
    setHunt((h) => ({ ...h, ...(typeof patch === "function" ? patch(h) : patch) }));
  }, []);

  const refreshThemeMeta = useCallback(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  const persistHunt = useCallback(
    async (nextHunt) => {
      if (!adminToken) {
        throw new Error("Session expired. Please log in again.");
      }
      // Never save before the real hunt has actually loaded once — huntLoadedRef only flips true
      // after a genuine (non-empty) response hydrates `hunt`. Without this, navigating a step (or
      // anything else that calls persistHunt) while still showing the blank DEFAULT_HUNT skeleton
      // — e.g. a slow/retrying load, or landing here right after Live Host — would happily write
      // that blank skeleton over the real saved checkpoints/rounds/teams, wiping a live hunt.
      if (!huntLoadedRef.current) {
        throw new Error("Still loading this hunt's saved data — please wait a moment and try again.");
      }
      setSaving(true);
      try {
        // Round content lives in the `questions` table now (see saveHuntQuestions.js), not the
        // flat themesdata blob — strip it out of the generic save and persist it separately.
        const { roundContent, ...flatFields } = nextHunt;
        const payload = {
          data: { ...flatFields },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        };
        await Promise.all([
          updateThemeData({ payload, token: adminToken }),
          saveHuntQuestions({
            payload: {
              organizationId: organizationId || "admin",
              sessionId: sessionId || "admin",
              currentTheme,
              roundContent: roundContent || [],
              checkpoints: nextHunt.checkpoints || [],
              lobby_pin: nextHunt.lobbyCode || null,
              lobby_link: nextHunt.lobbyCode
                ? buildJoinLink({ organizationId, sessionId, lobbyPin: nextHunt.lobbyCode }) || null
                : null,
            },
            token: adminToken,
          }),
        ]);
        setLastSavedAt(Date.now());
      } finally {
        setSaving(false);
      }
    },
    [adminToken, organizationId, sessionId, currentTheme]
  );

  // Persistence used to only happen on Next/Back/Publish — anything typed in between (a hunt
  // name, a checkpoint, a round's clue) lived only in this component's state, so a refresh or a
  // crash before the next step change lost it entirely. Debounce a save shortly after every edit
  // so in-progress work survives a refresh too, without hammering the backend on every keystroke.
  useEffect(() => {
    if (!huntLoadedRef.current) return;
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      return;
    }
    const t = setTimeout(() => {
      persistHunt(hunt).catch((e) => console.error("[Studio] autosave failed:", e.message));
    }, 1500);
    return () => clearTimeout(t);
  }, [hunt, persistHunt]);

  const goToStep = useCallback(
    async (nextStep) => {
      const clamped = clampStep(nextStep);
      const nextHunt = { ...hunt, wizardStep: clamped };
      skipNextAutosaveRef.current = true; // saved explicitly below — the debounced autosave would only duplicate it
      setHunt(nextHunt);
      setStep(clamped);
      setMaxStepReached((m) => Math.max(m, clamped));
      try {
        await persistHunt(nextHunt);
      } catch (e) {
        Swal.fire("Error", e.message || "Failed to save. Please try again.", "error");
      }
    },
    [hunt, persistHunt]
  );

  // Lets AdminTour (which lives outside this wizard's own state) jump the wizard to whichever
  // step its current tour card is pointing at, since every wizard step renders on the same
  // /admin/rules route rather than a distinct one AdminTour could navigate to directly. This is
  // purely a local view change for narration — NOT the same as goToStep, which persists a real
  // save. Routing this through goToStep used to fire a genuine backend save every time the tour
  // pointed at a different page, and if that save happened to race the hunt still (re)loading, it
  // threw "Still loading this hunt's saved data" and left the tour's target page never actually
  // switched — the wizard silently stayed on whatever step it was already showing, behind the
  // error dialog. Just showing the admin around never needs to write anything.
  useEffect(() => {
    const handler = (e) => {
      if (typeof e.detail !== "number") return;
      const clamped = clampStep(e.detail);
      setStep(clamped);
      setMaxStepReached((m) => Math.max(m, clamped));
    };
    window.addEventListener("admin-tour-goto-wizard-step", handler);
    return () => window.removeEventListener("admin-tour-goto-wizard-step", handler);
  }, []);

  const handleContinue = async () => {
    if (step >= STEP_DEFS.length) return;
    goToStep(step + 1);
  };
  const handleBack = () => {
    if (step <= 1) return;
    goToStep(step - 1);
  };
  // The lowest step whose own requirements are currently unmet, or null if every gate so far is
  // satisfied. Mirrors canContinue's per-step checks, but scans from the start rather than just
  // the step currently showing — needed because a step already unlocked (maxStepReached) can
  // still regress after the fact (e.g. changing a round's answer type un-approves it and can
  // invalidate its content for the new type), and the sidebar otherwise has no way to notice that.
  const firstBlockingStep = (h) => {
    const hasInstruction = Array.isArray(h.instructions) && h.instructions.some((s) => String(s ?? "").trim() !== "");
    const basicsOk = !!h.huntName.trim() && !!h.client.trim() && !!h.title.trim() && !!h.subHeading.trim() && hasInstruction;
    if (!basicsOk) return 1;

    if ((h.checkpoints || []).length < Math.max((h.teams || []).length, h.numRounds || 0)) return LOCATIONS_STEP_NUMBER;

    const rounds = h.rounds || [];
    const rc = h.roundContent || [];
    const roundsOk = rounds.length > 0 && rc.length === rounds.length && rounds.every((r, i) => isContentValidForType(rc[i], r.answerType));
    if (!roundsOk) return ROUNDS_STEP_NUMBER;

    if (!(rc.length > 0 && rc.every((r) => r.approved))) return REVIEW_STEP_NUMBER;

    return null;
  };

  const handleSidebarClick = (n) => {
    if (n > maxStepReached) return;
    // Jumping ahead of a gate that no longer holds (e.g. Review was unlocked earlier, but a round's
    // answer type just got changed back on the Rounds step, invalidating its content) must land on
    // the gate itself, not silently skip it — sidebar clicks bypass the footer's canContinue check
    // entirely otherwise, since "already unlocked" is all it normally looks at.
    const blocking = firstBlockingStep(hunt);
    if (blocking != null && n > blocking) {
      goToStep(blocking);
      return;
    }
    goToStep(n);
  };
  // Keep rounds/roundContent arrays in sync with numRounds. Depends on rounds.length too, not
  // just numRounds's value — hydration can replace the whole `hunt` object with a freshly loaded
  // `rounds` array (e.g. genuinely empty, for a brand-new theme) while numRounds itself stays at
  // the same value it already was, which would otherwise never re-trigger this effect and leave
  // the Round format table showing 6 selected but zero actual round rows.
  useEffect(() => {
    updateHunt((h) => {
      const n = h.numRounds;
      if (h.rounds.length === n) return {};
      const rounds = Array.from({ length: n }, (_, i) => h.rounds[i] || defaultRound(`r${i + 1}`));
      const roundContent = Array.from(
        { length: n },
        (_, i) => h.roundContent[i] || defaultRoundContent(rounds[i].id)
      );
      return { rounds, roundContent };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hunt.numRounds, hunt.rounds.length]);

  // Keep the team roster in sync with numTeams regardless of which step is showing —
  // Route assignment needs hunt.teams even if the admin never opens the Teams step. Same
  // hydration-timing issue as the rounds effect above: depend on teams.length too, not just
  // numTeams's value, so a freshly hydrated (possibly empty) `teams` array still gets synced even
  // when numTeams itself didn't change.
  useEffect(() => {
    updateHunt((h) => {
      if (h.teams && h.teams.length === h.numTeams) return {};
      return { teams: buildTeams(h.numTeams, h.teams || []) };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hunt.numTeams, hunt.teams.length]);

  // Regenerating checkpoints (toggling "Use suggested checkpoints", switching venue Setting)
  // gives every checkpoint a brand new ID even when the count stays the same — if that happens
  // after routes were already built and the admin never revisits Route Assignment before
  // publishing, the saved routes silently keep pointing at checkpoint IDs that no longer exist
  // anywhere, breaking both the "Your Route" name reveal and round content resolution for players.
  // Invalidate stale routes here, regardless of which step is showing, so Route Assignment
  // rebuilds them fresh the moment they're next needed.
  useEffect(() => {
    if (!hunt.routes || hunt.routes.length === 0) return;
    const validIds = new Set((hunt.checkpoints || []).map((c) => c.id));
    const stillValid = hunt.routes.every((r) => (r.checkpointOrder || []).every((id) => validIds.has(id)));
    if (!stillValid) {
      updateHunt({ routes: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hunt.checkpoints]);

  const draftSavedLabel = useMemo(() => timeAgoLabel(lastSavedAt), [lastSavedAt]);

  const canContinue = useMemo(() => {
    if (step === 1) {
      const hasInstruction = Array.isArray(hunt.instructions) && hunt.instructions.some((s) => String(s ?? "").trim() !== "");
      return (
        !!hunt.huntName.trim() &&
        !!hunt.client.trim() &&
        !!hunt.title.trim() &&
        !!hunt.subHeading.trim() &&
        hasInstruction
      );
    }
    if (step === LOCATIONS_STEP_NUMBER || step === ROUTES_STEP_NUMBER) {
      // Checkpoints have to cover two things at once: fewer than teams means at least two teams
      // must share one every round, and fewer than rounds means a single team's own route must
      // revisit a checkpoint it already stood at earlier — no route arrangement can avoid either.
      // Block it right here where it's caused (not just later in Route assignment) so the admin
      // adds more locations before moving on.
      const required = Math.max((hunt.teams || []).length, hunt.numRounds || 0);
      return (hunt.checkpoints || []).length >= required;
    }
    if (step === ROUNDS_STEP_NUMBER) {
      // Every round needs real generated (or manually written) content matching its answer type
      // before moving on — otherwise Review shows placeholder rows and the actual "nothing's been
      // generated yet" gate only fires later, from inside Review itself.
      const rounds = hunt.rounds || [];
      const rc = hunt.roundContent || [];
      return rounds.length > 0 && rc.length === rounds.length && rounds.every((r, i) => isContentValidForType(rc[i], r.answerType));
    }
    if (step !== REVIEW_STEP_NUMBER) return true;
    const rc = hunt.roundContent || [];
    return rc.length > 0 && rc.every((r) => r.approved);
  }, [step, hunt.huntName, hunt.client, hunt.title, hunt.subHeading, hunt.instructions, hunt.checkpoints, hunt.teams, hunt.numRounds, hunt.rounds, hunt.roundContent]);

  const commonStepProps = {
    hunt,
    updateHunt,
    themeMeta,
    adminToken,
    organizationId,
    sessionId,
    currentTheme,
    refreshThemeMeta,
    goToStep,
    persistHunt,
  };

  if (status === "loading" && !huntLoadedRef.current) {
    return (
      <div className="load-theme-container">
        <div className="load-theme-spinner"></div>
        <p>Loading studio...</p>
      </div>
    );
  }

  return (
    <div className="studio-shell">
      <aside className="studio-sidebar">
        <div className="studio-brand">
          <button className="studio-btn studio-btn--ghost" onClick={() => navigate("/admin")}>
            Back
          </button>
        </div>
        <ol className="studio-steps" data-tour="studio-sidebar-steps">
          {STEP_DEFS.map((s, i) => {
            const n = i + 1;
            const isDone = n < step;
            const isCurrent = n === step;
            const isReachable = n <= maxStepReached;
            return (
              <li
                key={s.key}
                className={
                  "studio-step" +
                  (isCurrent ? " studio-step--current" : "") +
                  (isDone ? " studio-step--done" : "") +
                  (!isReachable ? " studio-step--locked" : "")
                }
                onClick={() => handleSidebarClick(n)}
              >
                <span className="studio-step-badge">
                  {isDone ? <i className="fa-solid fa-check" /> : n}
                </span>
                <span className="studio-step-label">{s.label}</span>
              </li>
            );
          })}
        </ol>
      </aside>

      <div className="studio-main">
        <header className="studio-header">
          <div>
            <div className="studio-eyebrow">
              {hunt.huntName ? hunt.huntName.toUpperCase() : "UNTITLED HUNT"}
            </div>
            <h1 className="studio-title">
              {hunt.huntName ? hunt.huntName : "New treasure hunt"}
            </h1>
          </div>
          <div className="studio-header-actions">
            <span className="studio-draft-label">
              {saving ? "Saving..." : draftSavedLabel ? `Draft saved ${draftSavedLabel}` : ""}
            </span>
            <span className={`studio-live-status${liveStarted ? " studio-live-status--live" : ""}`}>
              <span className="studio-live-dot" />
              {liveStarted ? "Live" : "Not started"}
              {liveTeamsCount != null && ` · ${liveTeamsCount} team${liveTeamsCount === 1 ? "" : "s"} in`}
            </span>
            <button className="studio-btn studio-btn--ghost" data-tour="studio-live-host-btn" onClick={() => navigate("/admin/live")}>
              Live Host
            </button>
            <TourStartButton />
          </div>
        </header>

        <div className="studio-body">
          {step === 1 && <GameBasics {...commonStepProps} />}
          {step === 2 && <SettingLocations {...commonStepProps} />}
          {step === 3 && <RoundsContent {...commonStepProps} />}
          {step === 4 && <ReviewContent {...commonStepProps} />}
          {step === 5 && <Teams {...commonStepProps} />}
          {step === 6 && <RouteAssignment {...commonStepProps} />}
          {step === 7 && <Publish {...commonStepProps} onExit={() => navigate("/admin/live")} />}
        </div>

        {step < STEP_DEFS.length && (
          <footer className="studio-footer">
            <button className="studio-btn studio-btn--secondary" onClick={handleBack} disabled={step === 1 || saving}>
              Back
            </button>
            <button className="studio-btn studio-btn--primary" onClick={handleContinue} disabled={saving || !canContinue}>
              Continue to {STEP_DEFS[step]?.label.split(" ")[0].toLowerCase() || "next"}
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}

export default Studio;
